/**
 * SINGLE SOURCE OF TRUTH FOR REACTION EMOJIS
 * 
 * This file is the canonical list of all valid reaction emojis.
 * It uses CommonJS syntax so both server (Node.js) and frontend (via bundler) can import it.
 * 
 * TO ADD NEW REACTIONS:
 * 1. Add the emoji to REACTION_EMOJIS array below
 * 2. Add metadata to REACTION_META below
 * 3. Run database migration to update the CHECK constraint (see note below)
 * 
 * DATABASE NOTE:
 * The comment_reactions table has a CHECK constraint that must be updated via migration:
 * ALTER TABLE comment_reactions DROP CONSTRAINT comment_reactions_reaction_type_check;
 * ALTER TABLE comment_reactions ADD CONSTRAINT comment_reactions_reaction_type_check 
 *   CHECK (reaction_type IN ('👍', '👎', ... all emojis ...));
 */

// The canonical list of all valid reaction emojis
const REACTION_EMOJIS = [
  // Standard reactions
  '👍',  // Like/approve
  '👎',  // Dislike/disapprove
  '❤️',  // Love
  '😊',  // Happy/smile
  '😂',  // Funny/laughing
  '😮',  // Wow/surprised
  '🤔',  // Thinking/considering
  
  // Emphasis
  '💯',  // Perfect/100%
  '🔥',  // Hot/fire/great
  '🎉',  // Celebration/party
  
  // Status/action
  '✅',  // Done/approved/confirmed
  '❓',  // Question/need clarification
  '⚠️',  // Warning/attention needed
  '📌',  // Important/pinned
  
  // Business/property
  '💰',  // Money/financial
  '📈',  // Growth/trending up
  '👀',  // Looking/watching
  '💡',  // Idea/suggestion
];

// Metadata for each reaction (labels, categories, etc.)
const REACTION_META = {
  '👍': { label: 'Like', category: 'standard', shortcut: '+1' },
  '👎': { label: 'Dislike', category: 'standard', shortcut: '-1' },
  '❤️': { label: 'Love', category: 'standard' },
  '😊': { label: 'Happy', category: 'standard' },
  '😂': { label: 'Funny', category: 'standard' },
  '😮': { label: 'Wow', category: 'standard' },
  '🤔': { label: 'Thinking', category: 'standard' },
  '💯': { label: 'Perfect', category: 'emphasis' },
  '🔥': { label: 'Fire', category: 'emphasis' },
  '🎉': { label: 'Celebrate', category: 'emphasis' },
  '✅': { label: 'Done', category: 'action' },
  '❓': { label: 'Question', category: 'action' },
  '⚠️': { label: 'Attention', category: 'action' },
  '📌': { label: 'Important', category: 'action' },
  '💰': { label: 'Money', category: 'business' },
  '📈': { label: 'Growth', category: 'business' },
  '👀': { label: 'Looking', category: 'business' },
  '💡': { label: 'Idea', category: 'business' },
};

// Helper to get label for an emoji
function getReactionLabel(emoji) {
  return REACTION_META[emoji]?.label || emoji;
}

// Helper to check if an emoji is valid
function isValidReaction(emoji) {
  return REACTION_EMOJIS.includes(emoji);
}

// Helper to get emojis by category
function getReactionsByCategory(category) {
  return REACTION_EMOJIS.filter(emoji => REACTION_META[emoji]?.category === category);
}

// Generate SQL for database CHECK constraint
function generateSqlConstraint() {
  const quoted = REACTION_EMOJIS.map(e => `'${e}'`).join(', ');
  return `CHECK (reaction_type IN (${quoted}))`;
}

// Generate TypeScript type union
function generateTypeUnion() {
  return REACTION_EMOJIS.map(e => `'${e}'`).join(' | ');
}

// CommonJS exports (works with require() and import)
module.exports = {
  REACTION_EMOJIS,
  REACTION_META,
  getReactionLabel,
  isValidReaction,
  getReactionsByCategory,
  generateSqlConstraint,
  generateTypeUnion,
};

